import {isDefined, isNumber} from '../../../../common/service/data-util';

export const title = 'Angle Tools';
export const ddToDms = dd => {
    console.debug(title + ' : ddToDms');

    if (!isNumber(dd)) {
        if (dd.includes(':')) {
            return dd;
        }
    }

    const secondsPerMinute = 60;
    const minutesPerDegree = 60;
    const secondsPerDegree = secondsPerMinute * minutesPerDegree;

    const arcSeconds = Math.round((dd % 360) * secondsPerDegree);

    const seconds = arcSeconds % secondsPerMinute;
    const minutes = ((arcSeconds - seconds) / secondsPerMinute) % minutesPerDegree;
    const degrees = (arcSeconds - (arcSeconds % secondsPerDegree)) / secondsPerDegree;

    const ddd = degrees < 10 ? ('0' + degrees).slice(-2) : '' + degrees;
    const mm = minutes < 10 ? ('0' + minutes).slice(-2) : '' + minutes;
    const ss = seconds < 10 ? ('0' + seconds).slice(-2) : '' + seconds;

    return ddd + ':' + mm + ':' + ss;
};

export const dmsToDd = dms => {
    console.debug(title + ' : dmsToDd');

    const dmsParts = dms.split(/[^\d]+/);
    const degrees = parseInt(dmsParts[0]);
    const minutes = dmsParts.length >= 2 ? parseInt(dmsParts[1]) : 0;
    const seconds = dmsParts.length >= 3 ? parseInt(dmsParts[2]) : 0;

    return degrees + minutes / 60.0 + seconds / 3600.0;
};

export const ddToArcSeconds = dd => {
    return (dd % 360) * 3600;
};

export const dmsToArcSeconds = dms => {
    return ddToArcSeconds(dmsToDd(dms));
};

export const getBearing = (pointBearings, indexOfSortedBearing) => {
    console.debug(title + ' : getBearing');

    const heldbearings = [];

    for (const bearing in pointBearings) {
        heldbearings.push(bearing);
    }

    for (let x = 0; x < heldbearings.length - 1; x++) {
        for (let y = x + 1; y < heldbearings.length; y++) {
            if (isBearingLarger(heldbearings[x], heldbearings[y])) {
                const hold = heldbearings[y];
                heldbearings[y] = heldbearings[x];
                heldbearings[x] = hold;
            }
        }
    }

    return {
        lineId: pointBearings[heldbearings[indexOfSortedBearing]],
        bearing: heldbearings[indexOfSortedBearing]
    };
};

export const calcBearing = (x1, y1, x2, y2) => {
    const azimuth = Math.atan2(x2 - x1, y2 - y1);
    const azimuthInDegrees = (azimuth * 180) / Math.PI;
    let fixAzimuth = azimuthInDegrees % 360;

    if (fixAzimuth < 0) {
        fixAzimuth += 360;
    }

    return fixAzimuth;
};

export const subtractBearings = (bearing, bearingToSubtract) => {
    bearing = isNumber(bearing) ? bearing : dmsToDd(bearing);
    bearingToSubtract = isNumber(bearingToSubtract) ? bearingToSubtract : dmsToDd(bearingToSubtract);

    let arcSecondsDiff = ddToArcSeconds(bearing) - ddToArcSeconds(bearingToSubtract);
    if (arcSecondsDiff < 0) {
        arcSecondsDiff += 360 * 3600;
    }

    const ddDiff = arcSecondsDiff / 3600;
    return ddDiff;
};

export const calculateRotation = angle => {
    console.debug(title + ' : calculateRotation');

    let ddRotation = 0;
    if (isDefined(angle.attributes.anglerotationdms)) {
        ddRotation = dmsToDd(angle.attributes.anglerotationdms);
    } else if (isDefined(angle.attributes.rotationdms)) {
        ddRotation = dmsToDd(angle.attributes.rotationdms);
    }

    return (270 - ddRotation + 180) % 360;
};

export const getPoint = (points, id, data) => {
    let point = undefined;
    points.forEach(pointFeature => {
        if (pointFeature.attributes.id === id) {
            point = pointFeature;
        }
    });
    return point ? [point.attributes.x, point.attributes.y] : data;
};

export const subtract = (angle, angleToSubtract) => {
    if (isNumber(angle) && isNumber(angleToSubtract)) {
        return subtractBearings(angle, angleToSubtract);
    }
    return ddToDms(subtractBearings(angle, angleToSubtract));
};

export const subtractAngleFrom360 = angle => {
    return subtract(360, angle);
};

export const getAngleBearingAndRotationDetails = (point, fromLine, toLine, internal, points) => {
    console.debug(title + ' : getAngleBearingAndRotationDetails');

    let fromLineBearing,
        toLineBearing,
        fromLinePointFrom,
        fromLinePointTo,
        toLinePointFrom,
        toLinePointTo = undefined;

    if (!internal) {
        fromLinePointFrom =
            fromLine.attributes.frompointid === point.attributes.id
                ? getPoint(points, fromLine.attributes.frompointid, fromLine.geometry.paths[0][0])
                : getPoint(points, fromLine.attributes.topointid, fromLine.geometry.paths[0][1]);
        fromLinePointTo =
            fromLine.attributes.frompointid === point.attributes.id
                ? getPoint(points, fromLine.attributes.topointid, fromLine.geometry.paths[0][1])
                : getPoint(points, fromLine.attributes.frompointid, fromLine.geometry.paths[0][0]);

        toLinePointFrom =
            toLine.attributes.frompointid === point.attributes.id
                ? getPoint(points, toLine.attributes.frompointid, toLine.geometry.paths[0][0])
                : getPoint(points, toLine.attributes.topointid, toLine.geometry.paths[0][1]);
        toLinePointTo =
            toLine.attributes.frompointid === point.attributes.id
                ? getPoint(points, toLine.attributes.topointid, toLine.geometry.paths[0][1])
                : getPoint(points, toLine.attributes.frompointid, toLine.geometry.paths[0][0]);
    } else {
        fromLinePointFrom =
            toLine.attributes.frompointid === point.attributes.id
                ? getPoint(points, toLine.attributes.frompointid, toLine.geometry.paths[0][0])
                : getPoint(points, toLine.attributes.topointid, toLine.geometry.paths[0][1]);
        fromLinePointTo =
            toLine.attributes.frompointid === point.attributes.id
                ? getPoint(points, toLine.attributes.topointid, toLine.geometry.paths[0][1])
                : getPoint(points, toLine.attributes.frompointid, toLine.geometry.paths[0][0]);

        toLinePointFrom =
            fromLine.attributes.frompointid === point.attributes.id
                ? getPoint(points, fromLine.attributes.frompointid, fromLine.geometry.paths[0][0])
                : getPoint(points, fromLine.attributes.topointid, fromLine.geometry.paths[0][1]);
        toLinePointTo =
            fromLine.attributes.frompointid === point.attributes.id
                ? getPoint(points, fromLine.attributes.topointid, fromLine.geometry.paths[0][1])
                : getPoint(points, fromLine.attributes.frompointid, fromLine.geometry.paths[0][0]);
    }

    if (internal) {
        fromLineBearing = calcBearing(fromLinePointFrom[0], fromLinePointFrom[1], fromLinePointTo[0], fromLinePointTo[1]);
        toLineBearing = calcBearing(toLinePointFrom[0], toLinePointFrom[1], toLinePointTo[0], toLinePointTo[1]);
    } else {
        toLineBearing = calcBearing(fromLinePointFrom[0], fromLinePointFrom[1], fromLinePointTo[0], fromLinePointTo[1]);
        fromLineBearing = calcBearing(toLinePointFrom[0], toLinePointFrom[1], toLinePointTo[0], toLinePointTo[1]);
    }

    const subtractedBearings = subtractBearings(fromLineBearing, toLineBearing);

    return {
        bearingBetweenLines: subtractedBearings,
        rotation: (toLineBearing + subtractedBearings / 2) % 360
    };
};

export const decimalFormattedAngle = (dmsDegrees, dmsMinutes, dmsSeconds) => {
    console.debug(`${title} : decimalFormattedAngle`);

    const degrees = parseInt(dmsDegrees);
    const minutes = parseInt(dmsMinutes);
    const seconds = parseInt(dmsSeconds);

    const outMinutes = minutes || seconds ? dmsMinutes : '';
    const outSeconds = seconds ? dmsSeconds : '';
    const separator = minutes || seconds ? '.' : '';

    return `${degrees}${separator}${outMinutes}${outSeconds}`;
};

export const toDotDms = angle => {
    console.debug(`${title} : toDotDms : convert from dms to dd.mmss format`);

    if (angle) {
        const dmsParts = angle.split(/[^\d]+/);
        const degrees = dmsParts[0].toString();
        const minutes = dmsParts.length >= 2 ? dmsParts[1].toString() : 0;
        const seconds = dmsParts.length >= 3 ? dmsParts[2].toString() : 0;
        return decimalFormattedAngle(degrees, minutes, seconds);
    }
    return undefined;
};

export const toColonDms = angle => {
    console.debug(title + ' : toColonDms');

    // pad the minutes and seconds if they have only digit
    const dms = angle.split('.');
    let degrees,
        minutes,
        seconds = undefined;

    if (dms.length > 1) {
        if (dms[1].length === 4) {
            degrees = dms[0].toString();
            minutes = dms[1].substring(0, 2);
            seconds = dms[1].substring(2);

            return formattedAngle(degrees, minutes, seconds);
        } else if (dms[1].length === 2) {
            degrees = dms[0].toString();
            minutes = dms[1].toString();
            seconds = '0';

            return formattedAngle(degrees, minutes, seconds);
        }
    } else if (dms.length === 1) {
        degrees = dms[0].toString();
        minutes = '0';
        seconds = '0';

        return formattedAngle(degrees, minutes, seconds);
    }
    return undefined;
};

export const formattedAngle = (dmsDegrees, dmsMinutes, dmsSeconds) => {
    console.debug(title + ' : formattedAngle');

    const degrees = parseInt(dmsDegrees);
    const minutes = parseInt(dmsMinutes);
    const seconds = parseInt(dmsSeconds);

    let outDegrees = degrees.toString();
    let outMinutes = minutes.toString();
    let outSeconds = seconds.toString();

    if (degrees == 0) {
        outDegrees = '00';
    }

    if (degrees < 10 && degrees > 0) {
        outDegrees = '0' + outDegrees;
    }

    if (minutes == 0) {
        outMinutes = '00';
    }

    if (minutes < 10 && minutes > 0) {
        outMinutes = '0' + outMinutes;
    }

    if (seconds == 0) {
        outSeconds = '00';
    }

    if (seconds < 10 && seconds > 0) {
        outSeconds = '0' + outSeconds;
    }

    return outDegrees + ':' + outMinutes + ':' + outSeconds;
};

export const isBearingLarger = (bearingToTest, bearingToCompare) => {
    console.debug(title + ' : isBearingLarger');

    let bearingToTestDegrees = 0;
    let bearingToTestMinutes = 0;
    let bearingToTestSeconds = 0;
    if (bearingToTest.toString().indexOf(':') > 0) {
        const dms = bearingToTest.split(':');
        bearingToTestDegrees = parseInt(dms[0]);
        bearingToTestMinutes = parseInt(dms[1]);
        bearingToTestSeconds = parseInt(dms[2]);
    }

    let bearingToCompareDegrees = 0;
    let bearingToCompareMinutes = 0;
    let bearingToCompareSeconds = 0;
    if (bearingToCompare.toString().indexOf(':') > 0) {
        const bearingToCompareDMS = bearingToCompare.split(':');
        bearingToCompareDegrees = parseInt(bearingToCompareDMS[0]);
        bearingToCompareMinutes = parseInt(bearingToCompareDMS[1]);
        bearingToCompareSeconds = parseInt(bearingToCompareDMS[2]);
    }

    let degreeIsLarger = false;
    let minutesIsLarger = false;
    const secondsIsLarger = false;
    if (60 - bearingToTestDegrees < 60 - bearingToCompareDegrees) {
        degreeIsLarger = true;
    }
    if (60 - bearingToTestMinutes < 60 - bearingToCompareMinutes) {
        minutesIsLarger = true;
    }
    if (60 - bearingToTestSeconds < 60 - bearingToCompareSeconds) {
        minutesIsLarger = true;
    }

    if (degreeIsLarger) {
        return true;
    }

    if (bearingToTestDegrees === bearingToCompareDegrees) {
        if (minutesIsLarger) {
            return true;
        }
        if (bearingToTestMinutes === bearingToCompareMinutes) {
            if (secondsIsLarger) {
                return true;
            }
        }
    }

    return false;
};
//ND-1142 preventive solution - checking and rewriting internalangle values of angles to DD:MM:SS format
//This can be removed along with all of associated calls when all timeout features has been revised and has
// been made sure that no race condition may occur in an under-performing client computer
export const reCheckAngleFormats = features => {
    return features
        .map(angle => angle.attributes)
        .filter(attrs => attrs.internalangle && !attrs.internalangle.match(/[:.]/))
        .forEach(attrs => (attrs.internalangle = ddToDms(attrs.internalangle)));
};
